import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CumulativeSpendPoint, SpendDecile } from '@/services/org-admin/analysis';
import type {
  AdoptionBar,
} from '@/services/org-admin/overview';
import { CHART_GEOMETRY, useChartTheme } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

/**
 * The plots that exist only on the Overview screen.
 *
 * Kept together because they share one decision: all three are VERTICAL, where every plot on the
 * Analysis tab is horizontal. That is not a style choice — Analysis ranks named things (services,
 * people) whose labels need the horizontal room, while these three plot an ordered progression
 * (a funnel, a distribution, a month) where the x-axis carries the ordering and the labels are
 * short. Mixing the two orientations on one screen would be noise; mixing them between two screens
 * that answer different questions is the orientation doing its job.
 *
 * Colour comes from `useChartTheme` exactly as it does on Analysis: one blue for magnitude, red
 * reserved for spend past a budget. Nothing here introduces a third data hue.
 */

const DAY_LABEL = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const FULL_DATE_LABEL = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

/** `T00:00:00` keeps the date local — a bare `YYYY-MM-DD` parses as UTC and renders a day early. */
function parseDay(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

// ── Adoption funnel ─────────────────────────────────────────────────────────

export interface AdoptionFunnelChartProps {
  bars: AdoptionBar[];
  containerWidth: number | null;
}

/**
 * How many members are active, at each of the reporting windows that actually exist.
 *
 * The count sits ON each bar rather than only on the y-axis: this chart has four bars and the reader's
 * question is "how many", not "roughly what level" — an axis answers the second and makes them
 * estimate the first.
 */
export function AdoptionFunnelChart({ bars, containerWidth }: AdoptionFunnelChartProps) {
  const theme = useChartTheme();
  const isNarrow = containerWidth !== null && containerWidth < 420;

  const data = useMemo(
    () =>
      bars.map((bar) => ({
        ...bar,
        // The baseline bar is 100% by definition; printing that adds nothing and invites the
        // reader to treat it as a measured value rather than the denominator.
        shareLabel: bar.id === 'all' ? '' : `${Math.round(bar.shareOfAll * 100)}%`,
      })),
    [bars],
  );

  return (
    <ResponsiveContainer width="100%" height={isNarrow ? 180 : 218}>
      <BarChart data={data} margin={{ top: 30, right: 8, bottom: 0, left: -18 }} accessibilityLayer>
        <CartesianGrid stroke={theme.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={{ stroke: theme.grid }}
          interval={0}
        />
        <YAxis
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={false}
          width={40}
          // Members are whole people; a "2.5 members" tick is nonsense.
          allowDecimals={false}
          tickCount={4}
        />
        <Tooltip
          cursor={{ fill: theme.hoverBand }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const bar = payload[0]?.payload as AdoptionBar | undefined;
            if (!bar) return null;
            return (
              <ChartTooltip
                title={bar.label}
                rows={[
                  {
                    label: 'Members',
                    value: String(bar.members),
                    color: theme.magnitude,
                    note:
                      bar.id === 'all'
                        ? undefined
                        : `${Math.round(bar.shareOfAll * 100)}% of all members`,
                  },
                ]}
              />
            );
          }}
        />
        {/* Capped rather than left to fill the track: four bars stretched across a wide panel read as
            blocks of colour. A bar's LENGTH is the measure, and length is easier to compare when the
            bars are not also the widest thing on screen. */}
        <Bar
          dataKey="members"
          fill={theme.magnitude}
          radius={[3, 3, 0, 0]}
          maxBarSize={72}
          isAnimationActive={false}
        >
          {/*
            Count AND share, because the funnel's measure is the share and the count is the
            context. Labelling only the count made the reader subtract adjacent bars to find the
            drop-off, which is the one thing a funnel exists to show.
          */}
          <LabelList
            dataKey="members"
            position="top"
            fill={theme.axis}
            fontSize={CHART_GEOMETRY.tickFontSize}
          />
          <LabelList
            dataKey="shareLabel"
            position="top"
            offset={16}
            fill={theme.magnitude}
            fontSize={CHART_GEOMETRY.tickFontSize}
            fontWeight={600}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Spend concentration ─────────────────────────────────────────────────────

export interface ConcentrationChartProps {
  deciles: SpendDecile[];
  containerWidth: number | null;
}

/**
 * Share of spend by decile of member, highest first — "is this bill one person?".
 *
 * Empty deciles are plotted rather than dropped: the flat tail IS the finding. A chart that showed
 * only the three populated buckets would look like an even spread across the org.
 */
export function ConcentrationChart({ deciles, containerWidth }: ConcentrationChartProps) {
  const theme = useChartTheme();
  const isNarrow = containerWidth !== null && containerWidth < 420;

  const data = useMemo(
    () =>
      deciles.map((bucket) => ({
        ...bucket,
        // "Top" reads better than "0" for the leading bucket, which is the one being pointed at.
        tick: bucket.decile === 0 ? 'Top' : String(bucket.decile * 10),
        percent: Math.round(bucket.shareOfTotal * 100),
        percentLabel: `${Math.round(bucket.shareOfTotal * 100)}%`,
      })),
    [deciles],
  );

  return (
    <ResponsiveContainer width="100%" height={isNarrow ? 176 : 200}>
      <BarChart data={data} margin={{ top: 18, right: 8, bottom: 0, left: -22 }} accessibilityLayer>
        <CartesianGrid stroke={theme.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="tick"
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={{ stroke: theme.grid }}
          interval={0}
        />
        <YAxis hide domain={[0, 100]} />
        <Tooltip
          cursor={{ fill: theme.hoverBand }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const bucket = payload[0]?.payload as
              | (SpendDecile & { percentLabel: string })
              | undefined;
            if (!bucket) return null;
            return (
              <ChartTooltip
                title={bucket.decile === 0 ? 'Top 10% of spenders' : `${bucket.decile * 10}–${bucket.decile * 10 + 10}%`}
                rows={[
                  {
                    label: 'Share of spend',
                    value: bucket.percentLabel,
                    color: theme.magnitude,
                    note: `${bucket.members} member${bucket.members === 1 ? '' : 's'}`,
                  },
                ]}
              />
            );
          }}
        />
        <Bar dataKey="percent" radius={[3, 3, 0, 0]} maxBarSize={56} isAnimationActive={false}>
          {data.map((bucket) => (
            /*
             * Every bar is the same blue. The `Cell` exists only to drop the fill to a wash on empty
             * buckets so the populated ones carry the eye — it is not encoding a second variable,
             * which is why there is no second hue here.
             */
            <Cell
              key={bucket.decile}
              fill={bucket.percent > 0 ? theme.magnitude : theme.magnitudeFill}
            />
          ))}
          <LabelList
            dataKey="percentLabel"
            position="top"
            fill={theme.axis}
            fontSize={CHART_GEOMETRY.tickFontSize}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Cumulative spend ────────────────────────────────────────────────────────

export interface CumulativeSpendChartProps {
  points: CumulativeSpendPoint[];
  /** Sum of every seat's budget, drawn as the limit rule. Omitted when no budget is set. */
  budgetUsd: number;
  formatValue: (value: number) => string;
  formatAxis: (value: number) => string;
  containerWidth: number | null;
}

/**
 * Spend so far this window, accumulating, with the month-end projection carried on dashed.
 *
 * The two series are one line visually and two series structurally, because a projection drawn in
 * the same stroke as the actuals is a forecast presented as a fact. They are joined by repeating the
 * last ACTUAL point as the projection's first point — without it recharts leaves a gap at the seam
 * and the line appears to restart.
 */
export function CumulativeSpendChart({
  points,
  budgetUsd,
  formatValue,
  formatAxis,
  containerWidth,
}: CumulativeSpendChartProps) {
  const theme = useChartTheme();
  const isNarrow = containerWidth !== null && containerWidth < 520;
  const height = isNarrow ? CHART_GEOMETRY.trendHeightNarrow : CHART_GEOMETRY.trendHeightWide;

  const data = useMemo(() => {
    const lastActualIndex = points.reduce(
      (last, point, index) => (point.projected ? last : index),
      -1,
    );
    return points.map((point, index) => ({
      date: point.date,
      projected: point.projected,
      actualUsd: point.projected ? null : point.cumulativeUsd,
      // The seam: the last actual point belongs to BOTH series so the dashed line starts where the
      // solid one ends rather than a day later.
      projectedUsd: point.projected || index === lastActualIndex ? point.cumulativeUsd : null,
    }));
  }, [points]);

  const tickInterval = useMemo(() => {
    const maxTicks = isNarrow ? 4 : 6;
    return Math.max(0, Math.ceil(data.length / maxTicks) - 1);
  }, [data.length, isNarrow]);

  /*
   * The y-axis has to be told about the budget line.
   *
   * Recharts derives its domain from the SERIES only, so a `ReferenceLine` above the highest plotted
   * value is silently clipped — which is the one case where the line matters most, since an org
   * comfortably under budget is exactly an org whose spend never reaches the rule. The chart drew a
   * limit that could not be seen until spend had already passed it.
   *
   * Headroom on top so the line is not welded to the frame.
   */
  const upperBound = useMemo(() => {
    const dataMax = data.reduce(
      (max, row) => Math.max(max, row.actualUsd ?? 0, row.projectedUsd ?? 0),
      0,
    );
    return Math.ceil(Math.max(dataMax, budgetUsd) * 1.08);
  }, [data, budgetUsd]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: isNarrow ? -12 : 0 }} accessibilityLayer>
        <defs>
          <linearGradient id="oa-cumulative" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.magnitude} stopOpacity={0.26} />
            <stop offset="100%" stopColor={theme.magnitude} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke={theme.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="date"
          interval={tickInterval}
          tickFormatter={(iso: string) => DAY_LABEL.format(parseDay(iso))}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={{ stroke: theme.grid }}
          minTickGap={8}
        />
        <YAxis
          tickFormatter={formatAxis}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={false}
          width={isNarrow ? 44 : 56}
          tickCount={4}
          domain={[0, upperBound]}
        />

        {/*
          The budget rule, in the status colour — this is the one line on the screen that means
          "past here is bad", which is exactly what `theme.overage` is reserved for. Omitted rather
          than drawn at 0 when no seat has a budget, since a limit of zero is not a limit.
        */}
        {budgetUsd > 0 && (
          <ReferenceLine
            y={budgetUsd}
            stroke={theme.overage}
            strokeDasharray="5 4"
            strokeWidth={1.5}
            label={{
              value: formatAxis(budgetUsd),
              position: 'insideTopRight',
              fill: theme.overage,
              fontSize: CHART_GEOMETRY.tickFontSize,
            }}
          />
        )}

        <Tooltip
          cursor={{ fill: theme.hoverBand }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload as
              | { projected: boolean; actualUsd: number | null; projectedUsd: number | null }
              | undefined;
            if (!row) return null;
            const value = row.projected ? row.projectedUsd : row.actualUsd;
            if (typeof value !== 'number') return null;
            return (
              <ChartTooltip
                title={FULL_DATE_LABEL.format(parseDay(String(label)))}
                rows={[
                  {
                    label: row.projected ? 'Projected spend' : 'Spend to date',
                    value: formatValue(value),
                    color: theme.magnitude,
                    note: row.projected
                      ? 'Extends the last 7 days’ run rate'
                      : undefined,
                  },
                ]}
              />
            );
          }}
        />

        <Area
          type="monotone"
          dataKey="actualUsd"
          stroke={theme.magnitude}
          strokeWidth={2}
          fill="url(#oa-cumulative)"
          dot={false}
          activeDot={{ r: 3.5, strokeWidth: 2, stroke: theme.magnitude, fill: theme.magnitude }}
          // `false` so the two series do not bridge each other's nulls into one continuous line —
          // which would erase the distinction the dashes exist to make.
          connectNulls={false}
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="projectedUsd"
          stroke={theme.magnitude}
          strokeWidth={2}
          strokeDasharray="5 4"
          // No fill under the projection: a filled area reads as accumulated fact.
          fill="none"
          dot={false}
          activeDot={{ r: 3.5, strokeWidth: 2, stroke: theme.magnitude, fill: theme.magnitude }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
